import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Play, Award, TrendingUp } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { BrandLogo } from '../components/BrandLogo';
import RadarChart from '../components/RadarChart';
import { getCategoriesForPosition } from '../data/criteria';
import { PublicAPI } from '../api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmtSec(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export default function PublicProgressView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    PublicAPI.progress(token).then(setData).catch(e => {
      setErr(e?.response?.data?.detail || 'This share link is no longer valid.');
    });
  }, [token]);

  if (err) return <div className="min-h-screen bg-white flex items-center justify-center text-slate-600 p-6 text-center">{err}</div>;
  if (!data) return <div className="min-h-screen bg-white flex items-center justify-center text-slate-500">Loading</div>;

  const { player, overall, categories, recentEvaluations, topClips, evaluationCount } = data;
  const catList = getCategoriesForPosition(player.position || 'Forward');
  const radarData = catList.map(c => {
    const found = categories.find(x => x.categoryId === c.id);
    return { label: c.name, value: found ? found.score : 0 };
  });

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="public-progress-view">
      <header className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-2">
          <BrandLogo variant="inline" showTagline={false} />
          <Badge className="ml-auto bg-slate-100 text-slate-600 border-slate-200 text-[10px]">Read only</Badge>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Athlete card */}
        <div className="bg-[#0b1f4d] text-white rounded-2xl p-6 md:p-8 shadow-sm relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top_right,white,transparent_45%)]" />
          <div className="relative flex items-center gap-4">
            {player.avatar && (
              <img src={player.avatar} alt="" className="h-16 w-16 rounded-full ring-2 ring-white/30" />
            )}
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/70">Athlete</div>
              <div className="font-brand text-3xl uppercase" data-testid="progress-player-name">
                {player.firstName} {player.lastInitial}
              </div>
              <div className="text-sm text-white/80 mt-0.5">
                {player.position}{player.team ? `  ${player.team}` : ''}
                {player.ageGroup ? `  ${player.ageGroup}` : ''}
                {player.shoots ? `  Shoots ${player.shoots}` : ''}
              </div>
            </div>
            <div className="ml-auto text-right">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/70">Overall</div>
              <div className="font-brand text-5xl">{overall != null ? overall.toFixed(1) : ''}</div>
              <div className="text-[10px] text-white/60">{evaluationCount} coach evaluation{evaluationCount === 1 ? '' : 's'}</div>
            </div>
          </div>
        </div>

        {/* Category radar */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-[#1e40af]" />
            <h2 className="font-brand text-xl uppercase text-[#0b1f4d]">10-Category Snapshot</h2>
          </div>
          {radarData.length && radarData.some(d => d.value > 0) ? (
            <div className="flex justify-center">
              <RadarChart data={radarData} size={340} max={10} />
            </div>
          ) : (
            <div className="text-sm text-slate-500 py-10 text-center">Not enough evaluation data yet.</div>
          )}
        </div>

        {/* Recent evaluations */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Award className="h-4 w-4 text-amber-600" />
            <h2 className="font-brand text-xl uppercase text-[#0b1f4d]">Recent Evaluations</h2>
          </div>
          {recentEvaluations.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No coach evaluations yet.</div>
          ) : (
            <ul className="space-y-2" data-testid="progress-recent-evals">
              {recentEvaluations.map(e => (
                <li key={e.id} className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2.5">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{e.gameContext || 'Evaluation'}</div>
                    <div className="text-xs text-slate-500">{e.evaluatorName}</div>
                  </div>
                  <div className="text-xs text-slate-500 font-mono">
                    {e.date ? new Date(e.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Top clips */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Play className="h-4 w-4 text-[#1e40af]" fill="currentColor" />
            <h2 className="font-brand text-xl uppercase text-[#0b1f4d]">Top Tagged Clips</h2>
          </div>
          {topClips.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No tagged clips shared yet.</div>
          ) : (
            <div className="grid sm:grid-cols-3 gap-3" data-testid="progress-top-clips">
              {topClips.map(c => (
                <div key={c.videoId} className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
                  <div className="relative aspect-video bg-slate-900">
                    {c.source === 'youtube' ? (
                      <img
                        src={`https://img.youtube.com/vi/${c.youtubeId}/hqdefault.jpg`}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <video
                        src={`${BACKEND_URL}/api/videos/${c.videoId}/stream`}
                        muted preload="metadata"
                        className="w-full h-full object-cover"
                      />
                    )}
                    <div className="absolute bottom-1 left-1 font-mono text-[10px] text-white bg-black/70 px-1.5 py-0.5 rounded">{fmtSec(c.timestampSec)}</div>
                    {c.rating != null && (
                      <div className="absolute top-1 right-1 font-mono text-[10px] text-[#0b1f4d] bg-white/95 px-1.5 py-0.5 rounded font-bold">{c.rating}/10</div>
                    )}
                  </div>
                  <div className="p-2.5">
                    <div className="text-xs font-semibold text-slate-900 line-clamp-2">{c.label || c.title}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="text-center text-[11px] text-slate-400 py-6">
          Powered by ShiftNotes<sup className="text-[8px] text-[#1e40af]">&trade;</sup>  a product of NextShift Development LLC
        </div>
      </main>
    </div>
  );
}
